import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { parseDbDate } from "@totodo/operations"
import { useViewContext } from "@/app/stores/viewContext"
import { addProject, addList, addArea } from "@/db/operations"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { useClipboardStore } from "@/shared/stores/clipboardStore"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { useUndoStore, dismissUndoToast, registerUndo } from "@/shared/undo"
import {
  db,
  ObjectStatus,
  ObjectType,
  StartAtType,
  StartAtSubType,
} from "@/db/schema"
import {
  isMobile,
  markExternalKeyboard,
} from "@/shared/utils/platform/shortcutLabel"
import { useShortcutGuard } from "@/shared/hooks/useShortcutGuard"
import type { AddTaskOpts, InsertPosition } from "@/db/operations"

/** Group-separator IDs injected by GroupedTaskList to break selection radius. */
const isSeparator = (id: string) => id.startsWith("__group_sep__")

/** Views where paste/creation is blocked */
const NON_CREATABLE_VIEWS = new Set(["logbook", "trash", "search"])

// ---------------------------------------------------------------------------
// Focus restoration helper
// ---------------------------------------------------------------------------

/**
 * After removing tasks (complete / delete), focus the next visible task.
 * Prefers the task below; falls back to the task above. Skips separators.
 * Returns the ID of the task to focus, or null if none remain.
 */
function computeNextFocus(removingIds: string[]): string | null {
  const ids = useViewContext.getState().visibleTaskIds
  const removingSet = new Set(removingIds)

  // Find the lowest index among the tasks being removed
  let lowestIdx = ids.length
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]
    if (id != null && removingSet.has(id)) {
      lowestIdx = i
      break
    }
  }

  // Search forward for the next non-removed, non-separator
  for (let i = lowestIdx; i < ids.length; i++) {
    const id = ids[i]
    if (id != null && !removingSet.has(id) && !isSeparator(id)) return id
  }

  // Search backward
  for (let i = lowestIdx - 1; i >= 0; i--) {
    const id = ids[i]
    if (id != null && !removingSet.has(id) && !isSeparator(id)) return id
  }

  return null
}

/**
 * Focus next task after removing the given IDs. Convenience wrapper that
 * calls computeNextFocus + applies the result.
 */
function focusNextAfterRemoval(removingIds: string[]): void {
  const s = useViewContext.getState()
  const nextId = computeNextFocus(removingIds)
  if (nextId) {
    s.setSingleSelection(nextId)
  } else {
    s.clearSelection()
  }
  _shiftNavCursor = null
}

// ---------------------------------------------------------------------------
// Internal clipboard helpers
// ---------------------------------------------------------------------------

/** Resolve the selected task IDs (selected or last-clicked) */
function getSelectedIds(): string[] {
  const s = useViewContext.getState()
  if (s.selectedTaskIds.length > 0) return s.selectedTaskIds
  if (s.lastClickedTaskId) return [s.lastClickedTaskId]
  return []
}

/**
 * Expand section IDs to include their positional children.
 * Uses visibleTaskIds + visibleSectionIds from viewContext.
 * Non-section IDs pass through unchanged.
 */
function expandSectionChildren(ids: string[]): string[] {
  const s = useViewContext.getState()
  const allIds = s.visibleTaskIds
  const sections = s.visibleSectionIds
  if (sections.size === 0) return ids

  const result = new Set<string>()
  for (const id of ids) {
    result.add(id)
    if (sections.has(id)) {
      // Walk forward from section to collect children
      const idx = allIds.indexOf(id)
      if (idx === -1) continue
      for (let i = idx + 1; i < allIds.length; i++) {
        const childId = allIds[i]!
        if (isSeparator(childId) || sections.has(childId)) break
        result.add(childId)
      }
    }
  }
  return [...result]
}

/** Write task titles to the system clipboard as plain text */
async function writeTaskTitlesToSystemClipboard(
  taskIds: string[],
): Promise<void> {
  try {
    const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))
    const titles = tasks
      .filter((t): t is NonNullable<typeof t> => t != null)
      .map((t) => t.title)
      .join("\n")
    if (titles) {
      await navigator.clipboard.writeText(titles)
    }
  } catch {
    // Clipboard API can fail silently (e.g. no permission)
  }
}

/** Resolve parentPath for paste based on current view context */
function getPasteParentPath(): string {
  const s = useViewContext.getState()
  switch (s.viewId) {
    case "inbox":
      return "root/#inbox"
    case "today":
    case "evening":
    case "upcoming":
    case "anyday":
    case "someday":
      return "root/#anyday"
    case "project":
    case "list":
      return s.containerId ? `root/${s.containerId}` : "root/#inbox"
    case "area":
      return "root/#anyday"
    default:
      return "root/#inbox"
  }
}

/** Resolve insert position for paste (after selected, or top) */
function getPasteInsertPosition(): InsertPosition {
  const s = useViewContext.getState()
  return s.selectedTaskIds.length === 1
    ? { afterId: s.selectedTaskIds[0]! }
    : "top"
}

/** Duplicate tasks from internal clipboard into the current view */
async function pasteDuplicate(taskIds: string[]): Promise<number> {
  const { addTask } = await import("@/db/operations")
  const parentPath = getPasteParentPath()
  const insertPosition = getPasteInsertPosition()

  let count = 0
  for (const id of taskIds) {
    const orig = await db.totodo.get(id)
    if (!orig) continue

    const opts: AddTaskOpts = {
      title: orig.title,
      parentPath,
      insertPosition: count === 0 ? insertPosition : "top",
      type: orig.type as ObjectType,
      tags: [...orig.tags],
      dueAt: orig.dueAt,
      note: orig.note,
    }
    await addTask(opts)
    count++
  }
  return count
}

/** Move tasks from internal clipboard into the current view */
async function pasteMove(taskIds: string[]): Promise<number> {
  const { moveMultiple } = await import("@/db/operations")
  const parentPath = getPasteParentPath()
  const insertPosition = getPasteInsertPosition()

  const result = await moveMultiple(taskIds, parentPath, { insertPosition })
  if (result.ok) {
    return result.data?.count ?? taskIds.length
  }
  return 0
}

// ---------------------------------------------------------------------------
// Shift+Arrow range state
// ---------------------------------------------------------------------------

/**
 * Module-level state for Shift+Arrow range selection.
 * `_shiftNavCursor` tracks the "moving end" of the range.
 * `_shiftNavAnchor` records which anchor the cursor belongs to.
 * If `lastClickedTaskId` changes (e.g. a mouse click), the cursor
 * is automatically invalidated so the new anchor takes effect.
 */
let _shiftNavCursor: string | null = null
let _shiftNavAnchor: string | null = null

/**
 * Global keyboard shortcuts for the app.
 *
 * Input-mode awareness: when focus is in an input, textarea, or
 * contentEditable, only Escape and Ctrl/Meta combos are active.
 *
 * Shortcuts:
 * - `n`        — Focus the quick-add input
 * - `p`        — Create new project
 * - `l`        — Create new list
 * - `a`        — Create new area
 * - `1`–`6`,`i`— Navigate to views
 * - `Escape`   — Close task detail → close search → deselect
 * - `Ctrl+K`   — Toggle complete on selected/focused tasks (Things 3 ⌘K)
 * - `Alt+Ctrl+K`— Cancel selected/focused tasks (Things 3 ⌥⌘K)
 * - `Ctrl+T`   — Schedule selected tasks to Today (Things 3 ⌘T)
 * - `Ctrl+Y`   — Open status picker for selected/focused tasks
 * - `Ctrl+C`   — Copy selected tasks to internal clipboard + system clipboard
 * - `Ctrl+X`   — Cut selected tasks (copy + mark for move)
 * - `Ctrl+V`   — Paste: internal clipboard (duplicate/move) → system clipboard
 * - `Alt+Ctrl+V` — Always move (even if operation was copy)
 * - `Ctrl+F`   — Open search modal
 * - `Ctrl+S`   — Save & close task detail (prevent browser save)
 * - `Ctrl+,`   — Open settings (Mod+,)
 * - `Ctrl+\`   — Toggle desktop sidebar
 * - `↑` / `↓`  — Move focus cursor through tasks
 * - `Shift+↑/↓` — Extend selection (range) in arrow direction
 * - `Alt+Ctrl+↑/↓` — Move task to top/bottom of list
 * - `Ctrl+]`/`[` — Nudge scheduled date +1/−1 day
 * - `Shift+Ctrl+E` — Toggle show/hide logged items
 * - `Shift+Ctrl+N` — New heading after selected task
 * - `Enter`     — Open focused task detail
 * - `Space`     — New task after selected (positional)
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const guard = useShortcutGuard()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // On mobile devices, disable shortcuts unless an external keyboard is detected
      if (isMobile()) {
        markExternalKeyboard() // If we get a keydown, external KB must be connected
      }

      // Read all state imperatively — no subscriptions, no re-renders
      const s = useViewContext.getState()
      // Read active task from URL (source of truth) instead of Zustand
      const urlActiveTaskId = new URLSearchParams(window.location.search).get(
        "tid",
      )

      // Disabled during DnD
      if (s.isDragging) return

      // ── Shortcut context (P0–P3) ───────────────────────────────────
      const ctx = guard.getContext()
      const isInInput = ctx === "text-input"

      // P2: Modal/picker open → only Escape, Enter, Tab, and arrows allowed
      if (ctx === "modal-open" && !e.ctrlKey && !e.metaKey) {
        if (
          e.key !== "Escape" &&
          e.key !== "Enter" &&
          e.key !== "Tab" &&
          e.key !== "ArrowUp" &&
          e.key !== "ArrowDown" &&
          e.key !== "ArrowLeft" &&
          e.key !== "ArrowRight"
        ) {
          return
        }
      }

      // Blur guard: suppress non-modifier keys within 150ms of leaving a text input
      // Prevents phantom keystrokes (e.g. typing fast → blur → "n" triggers Quick Add)
      if (!e.ctrlKey && !e.metaKey && guard.isRecentlyBlurred()) return

      // ── Checklist context guard ─────────────────────────────────────
      // When focus is inside a checklist (editing or selecting), let the
      // checklist handlers own ⌘K / ⌘D / ⌘↑↓ / ⌘Backspace instead of
      // routing to global actions like search.
      const isInChecklist =
        (e.target as Element | null)?.closest("[data-checklist-container]") !==
        null

      // ── Ctrl/Meta combos (always active) ────────────────────────────

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "k": {
            if (isInChecklist) return // Handled by checklist
            e.preventDefault()

            // Alt+Mod+K: Cancel task (Things 3 ⌥⌘K)
            if (e.altKey) {
              const cancelIds = getSelectedIds()
              if (cancelIds.length === 0) return
              void (async () => {
                const { cancelTask, uncompleteTask } =
                  await import("@/db/operations")
                const { routeContainerStatus } = await import(
                  "@/features/project-header/projectStatusRequest"
                )
                for (const id of cancelIds) {
                  const task = await db.totodo.get(id)
                  if (!task) continue
                  if (task.status === ObjectStatus.Canceled) {
                    if (!(await routeContainerStatus(id, ObjectStatus.Unfinished))) await uncompleteTask(id)
                  } else if (!(await routeContainerStatus(id, ObjectStatus.Canceled))) {
                    await cancelTask(id)
                  }
                }
                toast(
                  cancelIds.length === 1
                    ? "Canceled task"
                    : `Canceled ${cancelIds.length} tasks`,
                )
              })()
              focusNextAfterRemoval(cancelIds)
              return
            }

            // Mod+K: toggle complete on selected/focused tasks (Things 3 ⌘K)
            const pendingAdd = usePendingCompletions.getState().add
            const taskIds =
              s.selectedTaskIds.length > 0
                ? s.selectedTaskIds
                : s.lastClickedTaskId
                  ? [s.lastClickedTaskId]
                  : []
            if (taskIds.length === 0) return
            // For each task, check current status and toggle
            void Promise.all(
              taskIds.map(async (id) => {
                const task = await db.totodo.get(id)
                if (!task) return
                if (
                  task.status === ObjectStatus.Completed ||
                  task.status === ObjectStatus.Canceled
                ) {
                  // Already done — uncomplete immediately
                  const { uncompleteTask } = await import("@/db/operations")
                  const { routeContainerStatus } = await import(
                    "@/features/project-header/projectStatusRequest"
                  )
                  if (!(await routeContainerStatus(id, ObjectStatus.Unfinished))) await uncompleteTask(id)
                } else {
                  // Unfinished — schedule completion with 2s delay
                  pendingAdd(id, "complete")
                }
              }),
            )
            // Focus the next task below (or above) the completed tasks
            focusNextAfterRemoval(taskIds)
            return
          }
          case "D": // fall-through: Shift turns "d" → "D"
          case "d": {
            if (e.shiftKey) {
              // Mod+Shift+D: Open deadline picker for selected tasks
              if (isInInput) return
              e.preventDefault()
              const deadlineIds = getSelectedIds()
              if (deadlineIds.length === 0) return
              useViewContext.getState().openDeadlinePicker(deadlineIds)
              return
            }
            // Mod+D: Duplicate selected tasks (Things 3 pattern)
            if (isInInput || isInChecklist) return
            e.preventDefault() // Prevent browser bookmark
            const dupIds = getSelectedIds()
            if (dupIds.length === 0) return
            void (async () => {
              const { duplicateTask } = await import("@/db/operations")
              const { INCOMPLETE_PART_LABELS, incompleteDuplicateParts } = await import(
                "@/features/task-actions/duplicateWarning"
              )
              const results = []
              for (const id of dupIds) {
                results.push(await duplicateTask(id))
              }
              toast(
                dupIds.length === 1
                  ? "Duplicated 1 task"
                  : `Duplicated ${dupIds.length} tasks`,
              )
              const parts = incompleteDuplicateParts(results)
              if (parts.length > 0) {
                toast.warning(
                  `Some content was not copied to the duplicate (${parts.map((p) => INCOMPLETE_PART_LABELS[p] ?? p).join(", ")}). Check the copy.`,
                )
              }
            })()
            return
          }
          case "e": {
            if (e.shiftKey) {
              // Mod+Shift+E: Toggle show/hide logged items
              if (isInInput) return
              e.preventDefault()
              document.dispatchEvent(new CustomEvent("totodo:toggle-logged"))
              return
            }
            // Mod+E: Schedule selected tasks to This Evening
            if (isInInput) return
            e.preventDefault() // Prevent browser address bar focus
            const eveningIds = getSelectedIds()
            if (eveningIds.length === 0) return
            void (async () => {
              const { scheduleTask } = await import("@/db/operations")
              const today = new Date().toISOString().split("T")[0]!
              for (const id of eveningIds) {
                await scheduleTask(
                  id,
                  today,
                  StartAtType.Default,
                  StartAtSubType.ThisEvening,
                )
              }
              toast(
                eveningIds.length === 1
                  ? "Scheduled for this evening"
                  : `Scheduled ${eveningIds.length} tasks for this evening`,
              )
            })()
            return
          }
          case "M": // fall-through: Shift turns "m" → "M"
          case "m": {
            // Mod+Shift+M: Open move-to-container picker
            if (!e.shiftKey) return
            if (isInInput) return
            e.preventDefault()
            const moveIds = getSelectedIds()
            if (moveIds.length === 0) return
            useViewContext.getState().openMovePicker(moveIds)
            return
          }
          case "T": // fall-through: Shift turns "t" → "T"
          case "t": {
            if (e.shiftKey) {
              // Mod+Shift+T: Open tag picker
              if (isInInput) return
              e.preventDefault()
              const tagIds = getSelectedIds()
              if (tagIds.length === 0) return
              useViewContext.getState().openTagPicker(tagIds)
              return
            }
            // Mod+T: Schedule to Today (Things 3 ⌘T)
            if (isInInput) return
            e.preventDefault()
            const todayIds = getSelectedIds()
            if (todayIds.length === 0) return
            void (async () => {
              const { scheduleTask } = await import("@/db/operations")
              const today = new Date().toISOString().split("T")[0]!
              for (const id of todayIds) {
                await scheduleTask(
                  id,
                  today,
                  StartAtType.Default,
                  StartAtSubType.Default,
                )
              }
              toast(
                todayIds.length === 1
                  ? "Scheduled for today"
                  : `Scheduled ${todayIds.length} tasks for today`,
              )
            })()
            return
          }
          case "o": {
            // Mod+O: Schedule selected tasks to Someday
            if (isInInput) return
            e.preventDefault() // Prevent browser file open
            const somedayIds = getSelectedIds()
            if (somedayIds.length === 0) return
            void (async () => {
              const { scheduleTask } = await import("@/db/operations")
              for (const id of somedayIds) {
                await scheduleTask(id, undefined, StartAtType.Someday)
              }
              toast(
                somedayIds.length === 1
                  ? "Moved to Someday"
                  : `Moved ${somedayIds.length} tasks to Someday`,
              )
            })()
            return
          }
          case "f":
            e.preventDefault()
            s.setSearchOpen(true)
            return
          case "j":
            e.preventDefault()
            s.setAiChatOpen(true)
            return
          case "s":
            e.preventDefault()
            // Close active task detail via URL (triggers blur → auto-save)
            if (urlActiveTaskId) {
              setSearchParams(
                (prev) => {
                  prev.delete("tid")
                  return prev
                },
                { replace: true },
              )
            }
            return
          case "N": // fall-through: Shift turns "n" → "N"
          case "n": {
            if (e.shiftKey) {
              // Mod+Shift+N: New heading after selected task (Things 3 ⇧⌘N)
              if (isInInput) return
              e.preventDefault()
              const focusedId = getSelectedIds()[0]
              if (!focusedId) return
              void (async () => {
                const { addTask } = await import("@/db/operations")
                const focusedTask = await db.totodo.get(focusedId)
                if (!focusedTask?.parentPath) return
                const result = await addTask({
                  title: "",
                  type: ObjectType.Section,
                  parentPath: focusedTask.parentPath,
                  insertPosition: { afterId: focusedId },
                })
                if (result.ok) {
                  useEditFocusStore.getState().requestEditFocus(result.data.id)
                  // Open the new section for title editing
                  setSearchParams(
                    (prev) => {
                      prev.set("tid", result.data.id)
                      return prev
                    },
                    { replace: true },
                  )
                }
              })()
              return
            }
            return
          }
          case "]":
          case "[": {
            // Mod+] / Mod+[: Nudge scheduled date +1 / −1 day
            if (isInInput) return
            e.preventDefault()
            const nudgeIds = getSelectedIds()
            if (nudgeIds.length === 0) return
            const nudgeDays = e.key === "]" ? 1 : -1
            void (async () => {
              const { scheduleTask } = await import("@/db/operations")
              const dayjs = (await import("dayjs")).default
              for (const id of nudgeIds) {
                const entry = await db.startAtIndex.get(`#${id}`)
                const entryMs = parseDbDate(entry?.startAt)
                if (!entry || entryMs === undefined) continue
                const newDate = dayjs(entryMs)
                  .add(nudgeDays, "day")
                  .format("YYYY-MM-DD")
                await scheduleTask(id, newDate, entry.type, entry.subType)
              }
              toast(
                nudgeDays === 1 ? "Date +1 day" : "Date −1 day",
              )
            })()
            return
          }
          case "y": {
            // Mod+Y: Open status picker for selected/focused tasks
            if (isInInput) return
            e.preventDefault()
            const statusIds = getSelectedIds()
            if (statusIds.length === 0) return
            useViewContext.getState().openStatusPicker(statusIds)
            return
          }
          case ",":
            e.preventDefault()
            // Open settings modal via query param
            setSearchParams(
              (prev) => {
                prev.set("setting", "general")
                return prev
              },
              { replace: true },
            )
            return
          case "\\":
            e.preventDefault()
            s.toggleDesktopSidebar()
            return
          case "a":
            // Mod+A: select all visible tasks cross-group
            // (only rendered tasks — not hidden behind "Show more")
            if (!isInInput) {
              e.preventDefault()
              s.selectAll()
            }
            return

          // ── Internal Clipboard: Cmd+C / Cmd+X / Cmd+V / Alt+Cmd+V ──

          case "C": {
            // Mod+Shift+C: Copy deep link to active task
            const activeId = s.activeTaskId
            if (!activeId) return
            e.preventDefault()
            const url = `${window.location.origin}/task/${activeId}`
            void navigator.clipboard.writeText(url).then(() => {
              toast("Link copied")
            })
            return
          }

          case "c": {
            // Mod+C: Copy selected tasks to internal clipboard + system clipboard
            if (isInInput) return // Let browser handle normal text copy
            const copyIds = expandSectionChildren(getSelectedIds())
            if (copyIds.length === 0) return // Nothing to copy
            e.preventDefault()
            useClipboardStore.getState().set(copyIds, "copy", s.viewId)
            void writeTaskTitlesToSystemClipboard(copyIds)
            toast(
              copyIds.length === 1
                ? "Copied 1 task"
                : `Copied ${copyIds.length} tasks`,
            )
            return
          }
          case "x": {
            // Mod+X: Cut selected tasks (copy + mark for move)
            if (isInInput) return // Let browser handle normal text cut
            const cutIds = expandSectionChildren(getSelectedIds())
            if (cutIds.length === 0) return
            e.preventDefault()
            useClipboardStore.getState().set(cutIds, "cut", s.viewId)
            void writeTaskTitlesToSystemClipboard(cutIds)
            toast(
              cutIds.length === 1 ? "Cut 1 task" : `Cut ${cutIds.length} tasks`,
            )
            return
          }
          case "v": {
            // Mod+V: Paste from internal clipboard → system clipboard fallback
            if (isInInput) return // Let browser handle normal text paste

            // Alt+Mod+V: always move (even if copied)
            const forceMove = e.altKey

            const clip = useClipboardStore.getState()

            // If internal clipboard has fresh content, handle it
            if (clip.hasContent()) {
              e.preventDefault() // Prevent paste event from firing

              if (NON_CREATABLE_VIEWS.has(s.viewId)) {
                toast.error("Cannot paste in this view")
                return
              }

              const shouldMove = forceMove || clip.operation === "cut"

              void (async () => {
                try {
                  const count = shouldMove
                    ? await pasteMove(clip.taskIds)
                    : await pasteDuplicate(clip.taskIds)

                  // Clear clipboard after cut/move
                  if (shouldMove) {
                    useClipboardStore.getState().clear()
                  }

                  toast(
                    shouldMove
                      ? count === 1
                        ? "Moved 1 task"
                        : `Moved ${count} tasks`
                      : count === 1
                        ? "Duplicated 1 task"
                        : `Duplicated ${count} tasks`,
                  )
                } catch (error) {
                  console.error("[clipboard] Paste failed:", error)
                  toast.error("Failed to paste tasks")
                }
              })()
              return
            }

            // No internal content → don't preventDefault → paste event fires
            // → useClipboardPaste handles system clipboard fallback
            return
          }

          // ── Undo / Redo: Cmd+Z / Cmd+Shift+Z ───────────────────────

          case "z": {
            // Context router: if editor/input focused → let TipTap/Yjs handle it
            if (isInInput || isInChecklist) return

            // isEditorFocused guard: TipTap has its own undo via Y.UndoManager.
            // Check if focus is inside a ProseMirror/TipTap editor element.
            const focusedEl = document.activeElement
            if (
              focusedEl?.closest(".ProseMirror") ||
              focusedEl?.closest(".tiptap")
            ) {
              return // Let TipTap handle Cmd+Z natively
            }

            e.preventDefault()

            if (e.shiftKey) {
              // Cmd+Shift+Z: Redo
              dismissUndoToast()
              void useUndoStore
                .getState()
                .redo()
                .then((entry) => {
                  if (entry) {
                    toast(entry.description)
                  }
                })
            } else {
              // Cmd+Z: Undo — dismiss toast (it references the same entry)
              dismissUndoToast()
              void useUndoStore
                .getState()
                .undo()
                .then((entry) => {
                  if (entry) {
                    toast(`Undo: ${entry.description}`)
                  }
                })
            }
            return
          }
        }
        return
      }

      // ── Escape (always active) ──────────────────────────────────────

      if (e.key === "Escape") {
        e.preventDefault()
        _shiftNavCursor = null
        _shiftNavAnchor = null
        if (s.activeTaskId || urlActiveTaskId) {
          // Close task detail via URL (source of truth)
          setSearchParams(
            (prev) => {
              prev.delete("tid")
              return prev
            },
            { replace: true },
          )
        } else if (s.searchOpen) {
          s.setSearchOpen(false)
        } else if (s.aiChatOpen) {
          s.setAiChatOpen(false)
        } else if (s.selectedTaskIds.length > 0) {
          s.clearSelection()
        }
        return
      }

      // ── Backspace / Delete: trash selected tasks ───────────────────
      // Only in list view (not in input, not in task detail, not in checklist).
      if (e.key === "Backspace" || e.key === "Delete") {
        if (isInInput || isInChecklist) return
        if (urlActiveTaskId) return // Let task-detail handler own this
        const delIds = getSelectedIds()
        if (delIds.length === 0) return
        // Compute next focus BEFORE removing (while visibleTaskIds still has them)
        const nextFocusId = computeNextFocus(delIds)
        e.preventDefault()
        void (async () => {
          const { moveToTrash, restoreFromTrash } =
            await import("@/db/operations")
          for (const id of delIds) {
            await moveToTrash(id, { silent: true })
          }
          // Register ONE batch undo so Ctrl+Z restores all tasks
          registerUndo(
            delIds.length === 1
              ? "Moved to trash"
              : `Moved ${delIds.length} tasks to trash`,
            async () => {
              for (const id of delIds) {
                await restoreFromTrash(id, { silent: true })
              }
            },
            async () => {
              for (const id of delIds) {
                await moveToTrash(id, { silent: true })
              }
            },
            "batch",
          )
          // Focus next task (computed before removal)
          if (nextFocusId) {
            s.setSingleSelection(nextFocusId)
            _shiftNavCursor = null
          } else {
            s.clearSelection()
            _shiftNavCursor = null
          }
        })()
        return
      }

      // ── Arrow key navigation (list focus) ──────────────────────────
      // ↑/↓ moves the focus cursor, Shift+↑/↓ adjusts selection range.
      // Cmd/Ctrl+↑/↓ reorders the focused task in-place.
      // The anchor (lastClickedTaskId) stays fixed during Shift+Arrow;
      // _shiftNavCursor tracks the moving end so Shift+↑ after Shift+↓
      // correctly shrinks the selection (macOS Finder / Things 3 pattern).
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        if (isInInput) return
        e.preventDefault()
        const ids = s.visibleTaskIds
        if (ids.length === 0) return

        const direction = e.key === "ArrowDown" ? 1 : -1

        // Helper: step in direction, skipping separator IDs (for plain arrow)
        const stepSkipping = (fromIdx: number): number => {
          let idx = fromIdx + direction
          while (idx >= 0 && idx < ids.length && isSeparator(ids[idx]!)) {
            idx += direction
          }
          return Math.max(0, Math.min(ids.length - 1, idx))
        }

        // ── Alt+Cmd/Ctrl+Arrow: move task to top/bottom ─────────────
        if ((e.metaKey || e.ctrlKey) && e.altKey && !e.shiftKey) {
          if (!s.isSortable) return
          const taskId = getSelectedIds()[0]
          if (!taskId) return

          void (async () => {
            try {
              const { reorderTaskDual, computeGhostIndex } =
                await import("@/db/operations")
              const { nextIndex: calcNextIndex } =
                await import("@/shared/utils/list/indexUtils")
              const { indexField } = useViewContext.getState()
              const ghostField: "index" | "startAtIndex" =
                indexField === "startAtIndex" ? "index" : "startAtIndex"

              // Find the first/last non-separator ID
              const edgeId =
                direction === -1
                  ? ids.find((id) => !isSeparator(id) && id !== taskId)
                  : [...ids]
                      .reverse()
                      .find((id) => !isSeparator(id) && id !== taskId)
              if (!edgeId) return

              // Compute index before/after edge
              let edgeSort: number | undefined
              if (indexField === "startAtIndex") {
                const entry = await db.startAtIndex.get(`#${edgeId}`)
                edgeSort = entry?.index
              } else {
                const t = await db.totodo.get(edgeId)
                edgeSort = t?.index
              }

              // Top: place before first (undefined, edgeSort)
              // Bottom: place after last (edgeSort, undefined)
              const newIdx =
                direction === -1
                  ? calcNextIndex(undefined, edgeSort)
                  : calcNextIndex(edgeSort, undefined)

              const movedTask = await db.totodo.get(taskId)
              const ghostIdx = await computeGhostIndex(
                ghostField,
                ghostField === "index" ? movedTask?.parentPath : undefined,
              )

              await reorderTaskDual(
                taskId,
                newIdx,
                indexField,
                ghostIdx,
                ghostField,
              )

              toast(direction === -1 ? "Moved to top" : "Moved to bottom")

              requestAnimationFrame(() => {
                const el =
                  document.getElementById(`scrolltarget-${taskId}`) ??
                  document.querySelector(`[data-task-id="${taskId}"]`)
                if (el) {
                  el.scrollIntoView({ block: "nearest", behavior: "smooth" })
                }
              })
            } catch (error) {
              console.error(
                "[useKeyboardShortcuts] Move to top/bottom failed:",
                error,
              )
            }
          })()
          return
        }

        // ── Cmd/Ctrl+Arrow: reorder task in the list ────────────────
        if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
          if (!s.isSortable) return // non-sortable views (logbook, trash, etc.)

          const taskId = getSelectedIds()[0]
          if (!taskId) return

          const currentIdx = ids.indexOf(taskId)
          if (currentIdx === -1) return

          // Find swap target (skip separators)
          let targetIdx = currentIdx + direction
          while (
            targetIdx >= 0 &&
            targetIdx < ids.length &&
            isSeparator(ids[targetIdx]!)
          ) {
            targetIdx += direction
          }
          if (targetIdx < 0 || targetIdx >= ids.length) return // at edge
          if (isSeparator(ids[targetIdx]!)) return

          void (async () => {
            try {
              const { reorderTaskDual, computeGhostIndex } =
                await import("@/db/operations")
              const { nextIndex: calcNextIndex } =
                await import("@/shared/utils/list/indexUtils")
              const { indexField } = useViewContext.getState()
              const ghostField: "index" | "startAtIndex" =
                indexField === "startAtIndex" ? "index" : "startAtIndex"

              // Build a list of IDs without the moved task
              const without = ids.filter((_, i) => i !== currentIdx)

              // In the without-array, the target position shifts
              const toIdx = direction === 1 ? targetIdx - 1 : targetIdx

              // Get sort indexes of neighbors around the target position
              // by querying the DB for each neighbor ID.
              const prevId = toIdx > 0 ? without[toIdx - 1] : undefined
              const nextId = toIdx < without.length ? without[toIdx] : undefined

              // Skip separators when reading neighbor IDs
              const resolveId = (id: string | undefined) =>
                id && !isSeparator(id) ? id : undefined

              const prevTaskId = resolveId(prevId)
              const nextTaskId = resolveId(nextId)

              // Fetch sort indexes from DB
              let prevSort: number | undefined
              let nextSort: number | undefined

              if (indexField === "startAtIndex") {
                if (prevTaskId) {
                  const entry = await db.startAtIndex.get(`#${prevTaskId}`)
                  prevSort = entry?.index
                }
                if (nextTaskId) {
                  const entry = await db.startAtIndex.get(`#${nextTaskId}`)
                  nextSort = entry?.index
                }
              } else {
                if (prevTaskId) {
                  const task = await db.totodo.get(prevTaskId)
                  prevSort = task?.index
                }
                if (nextTaskId) {
                  const task = await db.totodo.get(nextTaskId)
                  nextSort = task?.index
                }
              }

              const newIdx = calcNextIndex(prevSort, nextSort)

              // Compute ghost index for the other dimension
              const movedTask = await db.totodo.get(taskId)
              const ghostIdx = await computeGhostIndex(
                ghostField,
                ghostField === "index" ? movedTask?.parentPath : undefined,
              )

              await reorderTaskDual(
                taskId,
                newIdx,
                indexField,
                ghostIdx,
                ghostField,
              )

              // Scroll the task into view at its new position
              requestAnimationFrame(() => {
                const el =
                  document.getElementById(`scrolltarget-${taskId}`) ??
                  document.querySelector(`[data-task-id="${taskId}"]`)
                if (el) {
                  el.scrollIntoView({ block: "nearest", behavior: "smooth" })
                }
              })
            } catch (error) {
              console.error("[useKeyboardShortcuts] Reorder failed:", error)
            }
          })()
          return
        }

        if (e.shiftKey) {
          // ── Shift+Arrow: range from fixed anchor → moving cursor ────
          const anchorId = s.lastClickedTaskId
          if (!anchorId) {
            // No anchor yet — just single-select the first/last item
            let idx = direction === 1 ? 0 : ids.length - 1
            while (idx >= 0 && idx < ids.length && isSeparator(ids[idx]!))
              idx += direction
            if (idx < 0 || idx >= ids.length) return
            s.setSingleSelection(ids[idx]!)
            _shiftNavCursor = null
            _shiftNavAnchor = null
            return
          }

          // If the anchor changed (user clicked elsewhere), reset cursor
          if (_shiftNavAnchor !== anchorId) {
            _shiftNavCursor = null
            _shiftNavAnchor = anchorId
          }

          // Cursor starts at shift-nav position, or falls back to anchor
          const cursorId = _shiftNavCursor ?? anchorId
          const cursorIdx = ids.indexOf(cursorId)
          if (cursorIdx === -1) return

          // Move cursor one step — skip over group separators
          let nextCursorIdx = cursorIdx + direction
          while (
            nextCursorIdx >= 0 &&
            nextCursorIdx < ids.length &&
            isSeparator(ids[nextCursorIdx]!)
          ) {
            nextCursorIdx += direction
          }
          if (nextCursorIdx < 0 || nextCursorIdx >= ids.length) return // at edge
          const nextCursorId = ids[nextCursorIdx]!

          // Compute range from anchor → new cursor and REPLACE selection
          // Filter out separator IDs so they never enter selectedTaskIds
          const anchorIdx = ids.indexOf(anchorId)
          if (anchorIdx === -1) return
          const start = Math.min(anchorIdx, nextCursorIdx)
          const end = Math.max(anchorIdx, nextCursorIdx)
          const rangeIds = ids
            .slice(start, end + 1)
            .filter((id) => !isSeparator(id))

          useViewContext.setState({ selectedTaskIds: rangeIds })
          _shiftNavCursor = nextCursorId

          // Scroll the cursor into view
          const el =
            document.getElementById(`scrolltarget-${nextCursorId}`) ??
            document.querySelector(`[data-task-id="${nextCursorId}"]`)
          if (el) {
            el.scrollIntoView({ block: "nearest", behavior: "smooth" })
          }
        } else {
          // ── Plain arrow: single-select, reset shift cursor ──────────
          // Move from current position (shift cursor if active, else anchor)
          const currentId = _shiftNavCursor ?? s.lastClickedTaskId
          const currentIdx = currentId ? ids.indexOf(currentId) : -1

          let nextIdx: number
          if (currentIdx === -1) {
            nextIdx = direction === 1 ? 0 : ids.length - 1
          } else {
            nextIdx = stepSkipping(currentIdx)
          }
          const nextId = ids[nextIdx]!
          // If we landed on a separator (edge), don't move
          if (isSeparator(nextId)) return

          s.setSingleSelection(nextId)
          _shiftNavCursor = null
          _shiftNavAnchor = null

          // Scroll the focused task into view
          const el =
            document.getElementById(`scrolltarget-${nextId}`) ??
            document.querySelector(`[data-task-id="${nextId}"]`)
          if (el) {
            el.scrollIntoView({ block: "nearest", behavior: "smooth" })
          }
        }
        return
      }

      // Enter: open the focused (last-clicked) task
      if (e.key === "Enter" && !isInInput) {
        const focusedId = s.lastClickedTaskId
        if (focusedId && !urlActiveTaskId) {
          e.preventDefault()
          // Use URL params (source of truth) instead of Zustand
          setSearchParams(
            (prev) => {
              prev.set("tid", focusedId)
              return prev
            },
            { replace: true },
          )
          return
        }
      }

      // ── ←/→ arrow: open / close task detail ────────────────────────
      // → opens task detail (same as Enter). ← closes it (same as Escape).
      // Mirrors Things 3 / macOS Finder directional expand/collapse.
      if (e.key === "ArrowRight" && !isInInput) {
        const focusedId = s.lastClickedTaskId
        if (focusedId && !urlActiveTaskId) {
          e.preventDefault()
          setSearchParams(
            (prev) => {
              prev.set("tid", focusedId)
              return prev
            },
            { replace: true },
          )
          return
        }
      }
      if (e.key === "ArrowLeft" && !isInInput) {
        if (urlActiveTaskId) {
          e.preventDefault()
          setSearchParams(
            (prev) => {
              prev.delete("tid")
              return prev
            },
            { replace: true },
          )
          return
        }
      }

      // ── Single-key shortcuts (only when NOT in input mode) ──────────

      if (isInInput || e.altKey) return

      switch (e.key) {
        case " ": {
          // Space = new task after selected (Things 3 pattern)
          e.preventDefault()
          if (urlActiveTaskId) break // Don't create while task detail is open

          const focusedId = getSelectedIds()[0]
          if (!focusedId) {
            // No selection — fall back to QuickAdd focus
            const quickAddInput = document.querySelector<HTMLInputElement>(
              'input[placeholder="New task…"]',
            )
            if (quickAddInput) quickAddInput.focus()
            break
          }
          void (async () => {
            const { addTask } = await import("@/db/operations")
            const focusedTask = await db.totodo.get(focusedId)
            if (!focusedTask?.parentPath) return
            const result = await addTask({
              title: "",
              parentPath: focusedTask.parentPath,
              insertPosition: { afterId: focusedId },
            })
            if (result.ok) {
              useEditFocusStore.getState().requestEditFocus(result.data.id)
              setSearchParams(
                (prev) => {
                  prev.set("tid", result.data.id)
                  return prev
                },
                { replace: true },
              )
            }
          })()
          break
        }
        case "n": {
          e.preventDefault()
          const quickAddInput = document.querySelector<HTMLInputElement>(
            'input[placeholder="New task…"]',
          )
          if (quickAddInput) quickAddInput.focus()
          break
        }
        case "p":
          e.preventDefault()
          void addProject("").then((result) => {
            if (result.ok) {
              useEditFocusStore.getState().requestEditFocus(result.data.id)
              navigate(`/project/${result.data.id}`)
            }
          })
          break
        case "l":
          e.preventDefault()
          void addList("").then((result) => {
            if (result.ok) {
              useEditFocusStore.getState().requestEditFocus(result.data.id)
              navigate(`/list/${result.data.id}`)
            }
          })
          break
        case "a":
          e.preventDefault()
          void addArea("").then((result) => {
            if (result.ok) {
              useEditFocusStore.getState().requestEditFocus(result.data.id)
              navigate(`/area/${result.data.id}`)
            }
          })
          break
        case "1":
          e.preventDefault()
          navigate("/")
          break
        case "2":
          e.preventDefault()
          navigate("/evening")
          break
        case "3":
          e.preventDefault()
          navigate("/upcoming")
          break
        case "4":
          e.preventDefault()
          navigate("/anyday")
          break
        case "5":
          e.preventDefault()
          navigate("/someday")
          break
        case "6":
          e.preventDefault()
          navigate("/logbook")
          break
        case "i":
          e.preventDefault()
          navigate("/inbox")
          break
        case "t": {
          // t = toggle timer on focused/selected task
          e.preventDefault()
          const timerId = getSelectedIds()[0]
          if (!timerId) break
          void (async () => {
            const { startTimer, stopTimer } = await import("@/db/operations")
            const task = await db.totodo.get(timerId)
            if (!task) return
            if (task.timerStartedAt) {
              const result = await stopTimer(timerId)
              if (result.ok) {
                toast("Timer stopped")
              }
            } else {
              const result = await startTimer(timerId)
              if (result.ok) {
                toast("Timer started")
              }
            }
          })()
          break
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [navigate, setSearchParams, guard])
}
